import { SearchRounded } from "@mui/icons-material";
import { Box, Chip, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useLanguage } from "../../../../contexts/LanguageContext";
import translations from "./TagFilter.translations.json";
import { useEffect, useState } from "react";
import { getAllOfferTag } from "@services/api/offer-tag/getAllOfferTag";
import { Loading } from "@components/common/Loading";
import { useDebounce } from "@hooks/useDebounce";
import type { OfferTag } from "@components/ui/offer/public-list/types";
import { useSearchParams } from "react-router-dom";

type SupportedLanguage = keyof typeof translations;



interface TagFilterProps {
  selectedTag: string | null;
  onTagSelect: (tag: string) => void;
  categoryId?: number;
}

export const TagFilter = ({
  selectedTag,
  onTagSelect,
  categoryId,
}: TagFilterProps) => {
    const { language } = useLanguage();
    const [searchParams] = useSearchParams();
    const content = translations[language as SupportedLanguage] || translations.en;

    const targetCountry = searchParams.get("targetCountry") || "";

    const [tags, setTags] = useState<OfferTag[]>([]);
    const [loadingTag, setLoadingTag] = useState<boolean>(false);
    const [searchValue, setSearch] = useState<string>("");

    const debouncedSearch = useDebounce(searchValue);
  
    useEffect(() => {
        const handleFetchAllTags = async () => {
            setLoadingTag(true);
            const response = await getAllOfferTag({ search: debouncedSearch, targetCountry: targetCountry, categoryId });
            // Assuming you have a way to set the tags state
            setTags(response?.data || []);
            setLoadingTag(false);
        }

        handleFetchAllTags();
    }, [debouncedSearch, targetCountry, categoryId]);

 // const [tags, setTags] = useState<OfferTag[]>([]);

  return (
    <Box component="aside" sx={{ width: { xs: "100%", lg: 250 }, flexShrink: 0 }}>
        <Typography variant="overline" sx={{ color: "#8c94a4", fontWeight: 700, letterSpacing: "0.08em" }}>
        {content.filter}
        </Typography>
        <TextField
            fullWidth
            size="small"
            value={searchValue}
            onChange={(event) => {
                setSearch(event.target.value);
            }}
            placeholder={content.search}
            sx={{
                mt: 1.5,
                mb: 2,
                bgcolor: "#161d2b",
                "& .MuiInputBase-input": { color: "#ffffff" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1e2638" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6" },
            }}
            InputProps={{
                startAdornment: (
                <InputAdornment position="start">
                    <SearchRounded fontSize="small" color="action" />
                </InputAdornment>
                ),
            }}
        />
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
            <Chip
                label={content.all}
                clickable
                variant={selectedTag === undefined ? "filled" : "outlined"}
                onClick={() => onTagSelect("")}
                sx={{
                    color: selectedTag === undefined ? "#ffffff" : "#c8ced8",
                    borderColor: selectedTag === undefined ? "#3b82f6" : "#1e2638",
                    bgcolor: selectedTag === undefined ? "rgba(59, 130, 246, 0.2)" : "#161d2b",
                }}
            />
            {loadingTag && (
                <Loading />
            )}
            {!loadingTag && (
                <>
                    {tags.map((tag, index) => (
                        <Chip
                            key={`${tag}-${index}`}
                            label={`${tag.label} (${tag.count})`}
                            clickable
                            color={selectedTag === tag.label ? "primary" : undefined}
                            variant={selectedTag === tag.label ? "filled" : "outlined"}
                            onClick={() => onTagSelect(tag.label)}
                            sx={{
                                maxWidth: "100%",
                                color: selectedTag === tag.label ? "#ffffff" : "#c8ced8",
                                borderColor: selectedTag === tag.label ? "#3b82f6" : "#1e2638",
                                bgcolor: selectedTag === tag.label ? "rgba(59, 130, 246, 0.2)" : "#161d2b",
                            }}
                        />
                    ))}
                </>
            )}
        </Stack>
        {!loadingTag && tags.length === 0 && (
            <Typography variant="body2" sx={{ color: "#8c94a4" }}>
                {content.empty}
            </Typography>
        )}
    </Box>
  );
};
